import './ActionsWorkbench.css';
import '../common/ActionPresentation.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  Pagination,
  Select,
  Spin,
} from 'antd';
import {
  ArrowClockwise16Regular,
  ArrowCounterclockwise16Regular,
  CheckmarkCircle16Filled,
  Comment16Regular,
  Copy16Regular,
  Dismiss16Regular,
  DismissCircle16Filled,
  Document16Regular,
  Filter16Regular,
  Location16Regular,
  Organization16Regular,
  Person16Regular,
  Prohibited16Regular,
  Search16Regular,
  Timer16Regular,
} from '@fluentui/react-icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { dmsAntdTheme } from '../../config/antdTheme';

import actionRunsService, { CancelActionRunRequest } from '../../services/actionRunsService';
import {
  ActionArtifact,
  ActionRunDetail,
  ActionRunSummary,
  ActionTargetRun,
  PageResponseDto,
} from '../../types/actionTypes';
import {
  actionPhaseDisplayName,
  actionInitiatorIp,
  actionRunTitle,
  actionTargetLabel,
  actionTypeDisplayName,
  describeDownloadSelection,
  formatActionDateTime,
  getCancelPresentation,
} from '../../utils/actionPresentation';
import { formatDuration } from '../../utils/formatDuration';
import { noResultsTimeoutCopy, previousResultsWarningCopy, sanitizeApiError } from '../../utils/apiErrorSanitizer';
import ActionArtifactList from '../common/ActionArtifactList';
import { SkeletonDetailPanel, SkeletonRegion, SkeletonRow } from '../common/Skeleton/Skeleton';
import ActionLifecycleRow from '../common/ActionLifecycleRow';
import ActionStatusChip from '../common/ActionStatusChip';
import ActionStatusIcon from '../common/ActionStatusIcon';
import ActionTargetRow from '../common/ActionTargetRow';
import { RunningStatusBadge } from '../common/actionStatusGlyphs';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import { RootState } from '../../redux/store/store';
import { getActionStatusLabel, resolveActionDisplayStatus } from '../../utils/actionStatusConfig';
import { actionToActionRunDetail } from '../../utils/actionRunMapper';

const RUN_STATUSES = ['QUEUED', 'RUNNING', 'CANCELLING', 'SUCCEEDED', 'PARTIAL_SUCCESS', 'MIXED', 'FAILED', 'CANCELLED', 'TIMED_OUT', 'BLOCKED', 'OUTCOME_UNCONFIRMED'];
const ACTION_TYPES = ['REBOOT', 'UPDATE_CONFIG', 'UPDATE_DEPLOY', 'RTU_INFO', 'RTU_DEBUG_LOG', 'DOWNLOAD_CONFIG', 'DOWNLOAD_DEPLOY'];

const fluentIconProps = { 'aria-hidden': true as const, className: 'actions-workbench__fluent-icon' };

/**
 * Ant Select emits string option values directly. Keep a defensive unwrap for
 * any legacy object-shaped values so actionType/status are never sent as objects.
 */
const dropdownSelectValue = (value: unknown): string => {
  if (value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
    return String((value as { value?: unknown }).value ?? '');
  }
  return typeof value === 'string' ? value : '';
};

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
};

const parseNonNegativeInt = (value: string | null, fallback: number): number => {
  if (value === null || value.trim() === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

const dateInputValue = (value: string | null): string => value?.slice(0, 10) ?? '';
const utcBoundary = (value: string, end: boolean): string | undefined =>
  value ? `${value}T${end ? '23:59:59.999' : '00:00:00.000'}Z` : undefined;
const timestampValue = (value?: string): number => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

interface LiveRunFilters {
  q?: string;
  status?: string;
  actionType?: string;
  rtuId?: number;
  from?: string;
  to?: string;
}

const matchesLiveRunFilters = (run: ActionRunDetail, filters: LiveRunFilters): boolean => {
  if (filters.status && run.status !== filters.status) return false;
  if (filters.actionType && run.actionType !== filters.actionType) return false;
  if (filters.rtuId && !run.targetRuns?.some((target) => target.rtuId === filters.rtuId)) return false;

  const createdAt = timestampValue(run.createdAt);
  if (filters.from && createdAt < timestampValue(filters.from)) return false;
  if (filters.to && createdAt > timestampValue(filters.to)) return false;

  const query = filters.q?.trim().toLowerCase();
  if (!query) return true;
  const searchable = [
    run.id,
    run.userId,
    run.username,
    run.actionType,
    run.comment,
    run.fileName,
    ...(run.targetRuns ?? []).flatMap((target) => [
      target.rtuId,
      target.rtuSerialNo,
      target.rtuName,
      target.ip,
    ]),
  ].filter((value) => value !== undefined && value !== null)
    .join(' ')
    .toLowerCase();
  return searchable.includes(query);
};

const RunOutcomeIcons = ({ run }: { run: ActionRunSummary }) => {
  const { t } = useTranslation();
  const counts = {
    succeeded: run.successfulTargets ?? 0,
    failed: run.failedTargets ?? 0,
    running: run.runningTargets ?? 0,
    cancelled: run.cancelledTargets ?? 0,
    total: run.totalTargets ?? 0,
  };
  return (
    <span
      className="actions-workbench__outcomes"
      aria-label={t('actionsWorkbench.outcomes.summary', {
        defaultValue: '{{succeeded}} succeeded, {{failed}} failed, {{running}} running, {{cancelled}} cancelled, {{total}} visible targets',
        ...counts,
      })}
    >
      <span className="actions-workbench__outcome actions-workbench__outcome--ok" title={t('actionsWorkbench.outcomes.successfulTargets', { defaultValue: 'Successful targets' })}>
        <CheckmarkCircle16Filled {...fluentIconProps} />{counts.succeeded}
      </span>
      <span className="actions-workbench__outcome actions-workbench__outcome--failed" title={t('actionsWorkbench.outcomes.failedTargets', { defaultValue: 'Failed targets' })}>
        <DismissCircle16Filled {...fluentIconProps} />{counts.failed}
      </span>
      <span className="actions-workbench__outcome actions-workbench__outcome--running" title={t('actionsWorkbench.outcomes.runningTargets', { defaultValue: 'Running targets' })}>
        <RunningStatusBadge
          className="actions-workbench__fluent-icon"
          size={18}
          spin={counts.running > 0}
        />{counts.running}
      </span>
      <span className="actions-workbench__outcome actions-workbench__outcome--cancelled" title={t('actionsWorkbench.outcomes.cancelledTargets', { defaultValue: 'Cancelled targets' })}>
        <Prohibited16Regular {...fluentIconProps} />{counts.cancelled}
      </span>
      <span className="actions-workbench__outcome actions-workbench__outcome--total" title={t('actionsWorkbench.visibleTargets', { defaultValue: 'Visible targets' })}>
        <Organization16Regular {...fluentIconProps} />{counts.total}
      </span>
    </span>
  );
};

interface DetailPanelProps {
  detail: ActionRunDetail | null;
  loading: boolean;
  commandBusy: string | null;
  downloadingId: number | null;
  onRequestCancel: (detail: ActionRunDetail) => void;
  onCancelTarget: (target: ActionTargetRun) => void;
  onRetryTarget: (target: ActionTargetRun) => void;
  onDownload: (artifact: ActionArtifact) => void;
  onCopyId: (id: number) => void;
}

const ActionDetailPanel: React.FC<DetailPanelProps> = ({ detail, loading, commandBusy, downloadingId, onRequestCancel, onCancelTarget, onRetryTarget, onDownload, onCopyId }) => {
  const { t } = useTranslation();
  // Initial empty load only — never replace an already-visible detail with a
  // skeleton during background refresh / status refetch (DEC-ACT-082).
  if (loading && !detail) {
    return (
      <aside className="actions-workbench__detail">
        <SkeletonRegion label={t('actionsWorkbench.loadingAction', { defaultValue: 'Loading action...' })}>
          <SkeletonDetailPanel />
        </SkeletonRegion>
      </aside>
    );
  }
  if (!detail) {
    return <aside className="actions-workbench__detail actions-workbench__detail--empty">{t('actionsWorkbench.selectAction', { defaultValue: 'Select an action to inspect its visible targets and files.' })}</aside>;
  }

  const cancel = getCancelPresentation(detail.commandHints, t);
  const targetsById = Object.fromEntries((detail.targetRuns ?? []).map((target) => [target.id, actionTargetLabel(target, t)]));
  const artifactByTargetId = Object.fromEntries(
    (detail.artifacts ?? [])
      .filter((artifact) => artifact.targetRunId !== undefined)
      .map((artifact) => [artifact.targetRunId as number, artifact])
  );
  return (
    <aside className="actions-workbench__detail">
      <div className="actions-workbench__detail-header">
        <div>
          <h2>{actionRunTitle(detail, t)}</h2>
          <button type="button" className="actions-workbench__copy-id" onClick={() => onCopyId(detail.id)} title={t('actionsWorkbench.copyActionId', { defaultValue: 'Copy action ID' })}>
            <Copy16Regular {...fluentIconProps} /> {t('actionsWorkbench.actionIdValue', { defaultValue: 'Action ID {{id}}', id: detail.id })}
          </button>
        </div>
        <ActionStatusChip status={resolveActionDisplayStatus(detail)} />
      </div>

      <div className="actions-workbench__lifecycle-row">
        <strong>{actionPhaseDisplayName(detail.phaseLabel, detail.phaseCode, t)}</strong>
        <div className="actions-workbench__panel-actions">
          <Button
            type="text"
            size="small"
            danger
            icon={<Dismiss16Regular aria-hidden="true" />}
            loading={commandBusy === 'cancel-action'}
            disabled={!cancel.enabled || Boolean(commandBusy)}
            title={cancel.enabled ? cancel.label : cancel.reason}
            onClick={() => onRequestCancel(detail)}
          >
            {cancel.label}
          </Button>
        </div>
      </div>

      <ActionLifecycleRow createdAt={detail.createdAt} startedAt={detail.startedAt} completedAt={detail.completedAt} />

      <dl className="actions-workbench__meta">
        <div><dt><Person16Regular {...fluentIconProps} /> {t('actionsWorkbench.launchedBy', { defaultValue: 'Launched by' })}</dt><dd>{detail.username || detail.userId || t('actionsWorkbench.notReported', { defaultValue: 'Not reported' })}</dd></div>
        <div><dt><Location16Regular {...fluentIconProps} /> {t('actionsWorkbench.userIp', { defaultValue: 'User IP' })}</dt><dd>{actionInitiatorIp(detail) || t('actionsWorkbench.notReported', { defaultValue: 'Not reported' })}</dd></div>
        <div><dt><Timer16Regular {...fluentIconProps} /> {t('actionsWorkbench.duration', { defaultValue: 'Duration' })}</dt><dd>{detail.durationMs !== undefined ? formatDuration(detail.durationMs) : t('actionsWorkbench.notStarted', { defaultValue: 'Not started' })}</dd></div>
        <div><dt><Organization16Regular {...fluentIconProps} /> {t('actionsWorkbench.visibleTargets', { defaultValue: 'Visible targets' })}</dt><dd><RunOutcomeIcons run={detail} /></dd></div>
        {detail.retrySourceActionId ? <div><dt><ArrowCounterclockwise16Regular {...fluentIconProps} /> {t('actionsWorkbench.retryOf', { defaultValue: 'Retry of' })}</dt><dd>#{detail.retrySourceActionId}</dd></div> : null}
        {detail.fileName ? <div><dt><Document16Regular {...fluentIconProps} /> {t('actionsWorkbench.sourceFile', { defaultValue: 'Source file' })}</dt><dd>{detail.fileName}</dd></div> : null}
        {describeDownloadSelection(detail.actionType, detail.downloadSelection, t) ? <div><dt><Filter16Regular {...fluentIconProps} /> {t('actionsWorkbench.selection', { defaultValue: 'Selection' })}</dt><dd>{describeDownloadSelection(detail.actionType, detail.downloadSelection, t)}</dd></div> : null}
        {detail.comment ? <div className="actions-workbench__meta-wide"><dt><Comment16Regular {...fluentIconProps} /> {t('actionsWorkbench.comment', { defaultValue: 'Comment' })}</dt><dd>{detail.comment}</dd></div> : null}
      </dl>

      <section className="actions-workbench__targets">
        <h3>{t('actionsWorkbench.visibleTargets', { defaultValue: 'Visible targets' })}</h3>
        {(detail.targetRuns ?? []).length === 0 ? <div className="actions-workbench__empty-row">{t('actionsWorkbench.noAccessibleTargets', { defaultValue: 'No accessible targets are available.' })}</div> : detail.targetRuns?.map((target) => {
          const artifact = artifactByTargetId[target.id];
          return (
          <ActionTargetRow
            key={target.id}
            status={target.status}
            name={actionTargetLabel(target, t)}
            ip={target.ip || t('actionsWorkbench.notReported', { defaultValue: 'Not reported' })}
            phase={actionPhaseDisplayName(target.phaseLabel, target.phaseCode, t)}
            durationMs={target.durationMs}
            responseMessage={target.responseMessage}
            transfer={target.transfer}
            timeline={target.timeline}
            onDownloadArtifact={artifact ? () => onDownload(artifact) : undefined}
            downloadLabel={artifact ? t('actionsModal.artifacts.download', { defaultValue: 'Download {{fileName}}', fileName: artifact.fileName }) : undefined}
            downloadingArtifact={artifact ? downloadingId === artifact.id : false}
            artifactFileName={artifact?.fileName}
            activityHref={target.rtuId ? `/rtupage/${target.rtuId}?view=actions&actionRunId=${detail.id}` : undefined}
            menuActions={[
              {
                key: 'cancel',
                label: t('actionsWorkbench.cancel.target', { defaultValue: 'Cancel target' }),
                icon: 'pi pi-times',
                danger: true,
                disabled: !target.commandHints?.cancellable || Boolean(commandBusy),
                loading: commandBusy === `cancel-target-${target.id}`,
                onSelect: () => onCancelTarget(target),
              },
              {
                key: 'retry',
                label: t('actionsWorkbench.retryTarget', { defaultValue: 'Retry target' }),
                icon: 'pi pi-refresh',
                disabled: !target.commandHints?.retryable || Boolean(commandBusy),
                loading: commandBusy === `retry-target-${target.id}`,
                onSelect: () => onRetryTarget(target),
              },
            ]}
          />
          );
        })}
      </section>

      <ActionArtifactList artifacts={detail.artifacts ?? []} downloadingId={downloadingId} onDownload={onDownload} targetNameById={targetsById} />
    </aside>
  );
};

const ActionsWorkbench: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageResult, setPageResult] = useState<PageResponseDto<ActionRunSummary>>({ content: [], page: 0, size: 20, totalElements: 0, totalPages: 0 });
  const pageResultRef = useRef(pageResult);
  useEffect(() => { pageResultRef.current = pageResult; }, [pageResult]);
  const [page, setPage] = useState(() => parseNonNegativeInt(searchParams.get('page'), 0));
  const [size, setSize] = useState(() => Math.max(1, parseNonNegativeInt(searchParams.get('size'), 20)));
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [actionType, setActionType] = useState(searchParams.get('actionType') || '');
  const [rtuId, setRtuId] = useState(searchParams.get('rtuId') || '');
  const [fromDate, setFromDate] = useState(dateInputValue(searchParams.get('from')));
  const [toDate, setToDate] = useState(dateInputValue(searchParams.get('to')));
  const [selectedRunId, setSelectedRunId] = useState<number | null>(() => parseNonNegativeInt(searchParams.get('actionRunId') || searchParams.get('actionId'), 0) || null);
  const [detail, setDetail] = useState<ActionRunDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [hasLoadedListOnce, setHasLoadedListOnce] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [commandBusy, setCommandBusy] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [pendingCancel, setPendingCancel] = useState<ActionRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'list' | 'other' | null>(null);
  const [showFilters, setShowFilters] = useState(Boolean(query || status || actionType || rtuId || fromDate || toDate));
  const debouncedQuery = useDebouncedValue(query.trim(), 350);
  const actionSnapshots = useSelector((state: RootState) => state.actions.actions);
  // Read inside the loadRuns catch without becoming a reactive dependency:
  // a WebSocket-driven change to actionSnapshots (or pageResult updating
  // after every successful load) must never itself recreate loadRuns and
  // retrigger the mount effect - that would refetch on every WS snapshot,
  // which DEC-ACT-054 forbids.
  const actionSnapshotsRef = useRef(actionSnapshots);
  useEffect(() => { actionSnapshotsRef.current = actionSnapshots; }, [actionSnapshots]);
  const selectedRunIdRef = useRef(selectedRunId);
  useEffect(() => { selectedRunIdRef.current = selectedRunId; }, [selectedRunId]);
  const actionSnapshotById = useMemo(
    () => new Map(actionSnapshots.map((action) => [action.id, action])),
    [actionSnapshots],
  );

  const statusOptions = useMemo(() => [
    { label: t('actionsWorkbench.filters.allStatuses', { defaultValue: 'All statuses' }), value: '__ALL__' },
    ...RUN_STATUSES.map((value) => ({ label: getActionStatusLabel(value, t), value })),
  ], [t]);

  const actionTypeOptions = useMemo(() => [
    { label: t('actionsWorkbench.filters.allActionTypes', { defaultValue: 'All action types' }), value: '__ALL__' },
    ...ACTION_TYPES.map((value) => ({ label: actionTypeDisplayName(value, t), value })),
  ], [t]);

  const queryRequest = useMemo(() => ({
    page,
    size,
    q: debouncedQuery || undefined,
    status: status || undefined,
    actionType: actionType || undefined,
    rtuId: rtuId ? Number(rtuId) : undefined,
    from: utcBoundary(fromDate, false),
    to: utcBoundary(toDate, true),
  }), [actionType, debouncedQuery, fromDate, page, rtuId, size, status, toDate]);

  const syncUrl = useCallback((selectedId = selectedRunId) => {
    const next = new URLSearchParams();
    if (page) next.set('page', String(page));
    if (size !== 20) next.set('size', String(size));
    if (debouncedQuery) next.set('q', debouncedQuery);
    if (status) next.set('status', status);
    if (actionType) next.set('actionType', actionType);
    if (rtuId) next.set('rtuId', rtuId);
    if (queryRequest.from) next.set('from', queryRequest.from);
    if (queryRequest.to) next.set('to', queryRequest.to);
    if (selectedId) next.set('actionRunId', String(selectedId));
    setSearchParams(next, { replace: true });
  }, [actionType, debouncedQuery, page, queryRequest.from, queryRequest.to, rtuId, selectedRunId, setSearchParams, size, status]);

  const loadRuns = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    setErrorKind(null);
    try {
      const result = await actionRunsService.fetchActionRuns(queryRequest);
      setPageResult(result);
      // Read selection via ref so changing selectedRunId does not recreate
      // loadRuns / re-fire the mount list fetch (that was flashing the page).
      if (!selectedRunIdRef.current && result.content[0]) setSelectedRunId(result.content[0].id);
    } catch (loadError) {
      const sanitized = sanitizeApiError(loadError, { endpointCategory: 'action-list', t });
      // Rows can still be on screen even when this specific REST page fetch
      // failed, because the WebSocket-hydrated Redux snapshots (a parallel,
      // independent source) may already be populating the first page - never
      // judge "no previous results" from the REST page alone.
      const hasPreviousResults = pageResultRef.current.content.length > 0 || actionSnapshotsRef.current.length > 0;
      setError(hasPreviousResults
        ? previousResultsWarningCopy(t).message
        : (sanitized.category === 'timeout' || sanitized.category === 'network')
          ? noResultsTimeoutCopy(t).message
          : sanitized.message);
      setErrorKind('list');
    } finally {
      setLoadingList(false);
      setHasLoadedListOnce(true);
    }
    // `t` deliberately excluded - it is only used for error-message strings,
    // and including it here re-fired this fetch on every unrelated
    // Redux/i18next re-render (see src/locales/i18n.ts). Only queryRequest
    // should ever gate the list fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryRequest]);

  const loadDetail = useCallback(async (id: number) => {
    // Drop stale detail when switching actions so the initial-load skeleton
    // can show; keep the current detail when refreshing the same action.
    setDetail((current) => (current && current.id === id ? current : null));
    setLoadingDetail(true);
    try {
      setDetail(await actionRunsService.fetchActionRun(id));
    } catch (loadError) {
      const parsed = sanitizeApiError(loadError, { endpointCategory: 'action-detail', t });
      setDetail(null);
      setErrorKind('other');
      setError(parsed.status === 403
        ? t('actionsWorkbench.errors.actionAccessChanged', { defaultValue: 'You no longer have access to this action. The visible list has been refreshed.' })
        : parsed.message);
      if (parsed.status === 403 || parsed.status === 404) {
        setSelectedRunId(null);
        void loadRuns();
      }
    } finally {
      setLoadingDetail(false);
    }
    // `t` deliberately excluded - same reasoning as loadRuns above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRuns]);

  useEffect(() => { void loadRuns(); }, [loadRuns]);
  useEffect(() => { if (selectedRunId) void loadDetail(selectedRunId); else setDetail(null); }, [loadDetail, selectedRunId]);
  useEffect(() => { syncUrl(); }, [syncUrl]);

  const visibleRunModel = useMemo(() => {
    const serverIds = new Set(pageResult.content.map((run) => run.id));
    const mappedServerRuns = pageResult.content.flatMap((run) => {
      const snapshot = actionSnapshotById.get(run.id);
      if (!snapshot) return [run];
      const liveRun = actionToActionRunDetail(snapshot);
      return matchesLiveRunFilters(liveRun, queryRequest) ? [liveRun] : [];
    });
    const newlyVisibleRuns = page === 0
      ? actionSnapshots
        .filter((action) => !serverIds.has(action.id))
        .map(actionToActionRunDetail)
        .filter((run) => matchesLiveRunFilters(run, queryRequest))
      : [];
    const runs = [...newlyVisibleRuns, ...mappedServerRuns];
    if (page === 0) {
      runs.sort((left, right) => timestampValue(right.createdAt) - timestampValue(left.createdAt));
    }
    return {
      runs: runs.slice(0, size),
      totalRecords: Math.max(
        0,
        pageResult.totalElements + newlyVisibleRuns.length - (pageResult.content.length - mappedServerRuns.length),
      ),
    };
  }, [
    actionSnapshotById,
    actionSnapshots,
    page,
    pageResult.content,
    pageResult.totalElements,
    queryRequest,
    size,
  ]);
  const visibleRuns = visibleRunModel.runs;
  const visibleDetail = selectedRunId && actionSnapshotById.has(selectedRunId)
    ? actionToActionRunDetail(actionSnapshotById.get(selectedRunId)!)
    : detail;

  const selectRun = (id: number) => { setSelectedRunId(id); syncUrl(id); };
  const resetPage = (change: () => void) => { change(); setPage(0); };

  const initiatedRange: [Dayjs | null, Dayjs | null] | null = fromDate || toDate
    ? [fromDate ? dayjs(fromDate) : null, toDate ? dayjs(toDate) : null]
    : null;
  const handleInitiatedRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    resetPage(() => {
      setFromDate(dates?.[0] ? dates[0].format('YYYY-MM-DD') : '');
      setToDate(dates?.[1] ? dates[1].format('YYYY-MM-DD') : '');
    });
  };

  const performCancel = async () => {
    if (!pendingCancel) return;
    const presentation = getCancelPresentation(pendingCancel.commandHints, t);
    if (!presentation.enabled || presentation.scope === 'NONE') return;
    const request: CancelActionRunRequest = {
      cancelScope: presentation.scope,
      targetRunIds: pendingCancel.commandHints?.cancellableTargetRunIds ?? [],
      scopeRevision: pendingCancel.commandHints?.scopeRevision ?? '',
    };
    setPendingCancel(null);
    setCommandBusy('cancel-action');
    setError(null);
    setErrorKind(null);
    try {
      await actionRunsService.cancelActionRun(pendingCancel.id, request);
      await Promise.all([loadRuns(), loadDetail(pendingCancel.id)]);
    } catch (cancelError) {
      const parsed = sanitizeApiError(cancelError, { endpointCategory: 'action-cancel', t });
      setErrorKind('other');
      if (parsed.status === 409 && parsed.code === 'ACTION_CANCEL_SCOPE_CHANGED') {
        await Promise.all([loadRuns(), loadDetail(pendingCancel.id)]);
        setError(t('actionsWorkbench.errors.cancelScopeChanged', { defaultValue: 'Action scope changed. Review the refreshed targets before confirming cancellation again.' }));
      } else if (parsed.status === 409) {
        setError(parsed.message || t('actionsWorkbench.errors.cancelTooLate', { defaultValue: 'Cancellation arrived too late for the current action phase.' }));
        await loadDetail(pendingCancel.id);
      } else if (parsed.status === 403) {
        setSelectedRunId(null);
        await loadRuns();
        setError(t('actionsWorkbench.errors.actionAccessRefreshed', { defaultValue: 'Your access to this action changed. The visible action list has been refreshed.' }));
      } else setError(parsed.message);
    } finally {
      setCommandBusy(null);
    }
  };

  const runTargetCommand = async (target: ActionTargetRun, command: 'cancel' | 'retry') => {
    if (!visibleDetail) return;
    setCommandBusy(`${command}-target-${target.id}`);
    setError(null);
    setErrorKind(null);
    try {
      if (command === 'cancel') await actionRunsService.cancelActionTargetRun(target.id);
      else await actionRunsService.retryActionTargetRun(target.id);
      await Promise.all([loadRuns(), loadDetail(visibleDetail.id)]);
    } catch (targetError) {
      const parsed = sanitizeApiError(targetError, { endpointCategory: 'action-target-command', t });
      setErrorKind('other');
      if (parsed.status === 403) {
        setSelectedRunId(null);
        await loadRuns();
        setError(t('actionsWorkbench.errors.targetAccessChanged', { defaultValue: 'Your access to this target changed. The visible action list has been refreshed.' }));
      } else if (parsed.status === 409) {
        await loadDetail(visibleDetail.id);
        setError(parsed.message || t('actionsWorkbench.errors.targetStateChanged', { defaultValue: 'The target state changed before the command was accepted.' }));
      } else {
        setError(parsed.message);
      }
    } finally {
      setCommandBusy(null);
    }
  };

  const downloadArtifact = async (artifact: ActionArtifact) => {
    if (!visibleDetail) return;
    setDownloadingId(artifact.id);
    try { await actionRunsService.downloadArtifact(visibleDetail.id, artifact); }
    catch (downloadError) {
      setErrorKind('other');
      setError(sanitizeApiError(downloadError, { endpointCategory: 'artifact-download', t }).message);
    }
    finally { setDownloadingId(null); }
  };

  const copyId = async (id: number) => {
    try { await navigator.clipboard.writeText(String(id)); }
    catch { setError(t('actionsWorkbench.actionIdFallback', { defaultValue: 'Action ID: {{id}}', id })); }
  };

  const hasFilters = Boolean(query || status || actionType || rtuId || fromDate || toDate);
  const searchPending = query.trim() !== debouncedQuery;
  const searchLoading = searchPending || (loadingList && (searchPending || Boolean(debouncedQuery)));

  return (
    <ConfigProvider direction={i18n.dir()} theme={dmsAntdTheme}>
      <main className="actions-workbench">
        <h1 className="dms-visually-hidden">{t('actionsWorkbench.title', { defaultValue: 'All Actions' })}</h1>
        <section className="actions-workbench__command-bar" aria-label={t('actionsWorkbench.commandBar', { defaultValue: 'Action filters and controls' })}>
          <div className="actions-workbench__command-group actions-workbench__command-group--search">
            <Input
              className="actions-workbench__search"
              value={query}
              onChange={(event) => resetPage(() => setQuery(event.target.value))}
              placeholder={t('actionsWorkbench.filters.search', { defaultValue: 'Search actions' })}
              aria-label={t('actionsWorkbench.filters.search', { defaultValue: 'Search actions' })}
              aria-busy={searchLoading}
              prefix={searchLoading ? <Spin size="small" /> : <Search16Regular aria-hidden="true" />}
              allowClear
            />
          </div>

          {showFilters ? (
            <>
              <div className="actions-workbench__command-group">
                <Select
                  className="actions-workbench__filter-control"
                  popupClassName="actions-workbench__filter-panel"
                  virtual={false}
                  value={status || '__ALL__'}
                  options={statusOptions}
                  onChange={(value) => resetPage(() => setStatus(value === '__ALL__' ? '' : dropdownSelectValue(value)))}
                  placeholder={t('actionsWorkbench.filters.status', { defaultValue: 'Status' })}
                  aria-label={t('actionsWorkbench.filters.status', { defaultValue: 'Status' })}
                />
              </div>
              <div className="actions-workbench__command-group">
                <Select
                  className="actions-workbench__filter-control actions-workbench__filter-control--wide"
                  popupClassName="actions-workbench__filter-panel"
                  virtual={false}
                  value={actionType || '__ALL__'}
                  options={actionTypeOptions}
                  onChange={(value) => resetPage(() => setActionType(value === '__ALL__' ? '' : dropdownSelectValue(value)))}
                  placeholder={t('actionsWorkbench.filters.actionType', { defaultValue: 'Action type' })}
                  aria-label={t('actionsWorkbench.filters.actionType', { defaultValue: 'Action type' })}
                />
              </div>
              <div className="actions-workbench__command-group">
                <Input
                  className="actions-workbench__filter-control"
                  inputMode="numeric"
                  value={rtuId}
                  onChange={(event) => resetPage(() => setRtuId(event.target.value.replace(/\D/g, '')))}
                  placeholder={t('actionsWorkbench.filters.exactRtuId', { defaultValue: 'Exact RTU ID' })}
                  aria-label={t('actionsWorkbench.filters.exactRtuId', { defaultValue: 'Exact RTU ID' })}
                  allowClear
                />
              </div>
              <div className="actions-workbench__command-group actions-workbench__command-group--dates">
                <DatePicker.RangePicker
                  className="actions-workbench__date-range"
                  value={initiatedRange}
                  onChange={(dates) => handleInitiatedRangeChange(dates as [Dayjs | null, Dayjs | null] | null)}
                  allowClear
                  aria-label={t('actionsWorkbench.filters.initiatedDate', { defaultValue: 'Initiated date' })}
                  placeholder={[
                    t('actionsWorkbench.filters.initiatedFrom', { defaultValue: 'Initiated from' }),
                    t('actionsWorkbench.filters.initiatedTo', { defaultValue: 'Initiated to' }),
                  ]}
                />
              </div>
              <div className="actions-workbench__command-group">
                <Button
                  type="text"
                  size="small"
                  icon={<Dismiss16Regular aria-hidden="true" />}
                  disabled={!hasFilters}
                  onClick={() => { setQuery(''); setStatus(''); setActionType(''); setRtuId(''); setFromDate(''); setToDate(''); setPage(0); }}
                >
                  {t('actionsWorkbench.filters.clear', { defaultValue: 'Clear' })}
                </Button>
              </div>
            </>
          ) : null}

          <span className="actions-workbench__command-spacer" aria-hidden="true" />

          {loadingList && !hasLoadedListOnce ? (
            <span className="actions-workbench__loading">
              <Spin size="small" /> {t('actionsWorkbench.loading', { defaultValue: 'Loading' })}
            </span>
          ) : null}
          <div className="actions-workbench__command-group actions-workbench__command-group--actions">
            <Button
              type="text"
              size="small"
              icon={<Filter16Regular aria-hidden="true" />}
              onClick={() => setShowFilters((value) => !value)}
              aria-expanded={showFilters}
            >
              {t('actionsWorkbench.filters.toggle', { defaultValue: 'Filters' })}
            </Button>
            <Button
              type="text"
              size="small"
              icon={<ArrowClockwise16Regular aria-hidden="true" />}
              onClick={() => void loadRuns()}
            >
              {t('actionsWorkbench.refresh', { defaultValue: 'Refresh' })}
            </Button>
          </div>
        </section>

        {error ? (
          <Alert
            className={`actions-workbench__error${errorKind === 'list' ? ' actions-workbench__error--compact' : ''}`}
            type={errorKind === 'list' && visibleRuns.length > 0 ? 'warning' : 'error'}
            showIcon
            banner={errorKind === 'list' && visibleRuns.length > 0}
            message={error}
            action={(
              <div className="actions-workbench__error-actions">
                <Button
                  type="link"
                  size="small"
                  icon={<ArrowClockwise16Regular aria-hidden="true" />}
                  onClick={() => { setError(null); setErrorKind(null); void loadRuns(); }}
                >
                  {t('apiErrors.tryAgain', { defaultValue: 'Try again' })}
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<Dismiss16Regular aria-hidden="true" />}
                  onClick={() => { setError(null); setErrorKind(null); }}
                  aria-label={t('actionsWorkbench.dismissMessage', { defaultValue: 'Dismiss message' })}
                />
              </div>
            )}
          />
        ) : null}

        <section className="actions-workbench__layout">
          <div className="actions-workbench__list" role="list">
            {loadingList && !hasLoadedListOnce ? (
              <SkeletonRegion label={t('actionsWorkbench.loading', { defaultValue: 'Loading' })}>
                <SkeletonRow count={8} />
              </SkeletonRegion>
            ) : null}
            {!(loadingList && !hasLoadedListOnce) && visibleRuns.length === 0 && !loadingList ? <div className="actions-workbench__empty-row">{t('actionsWorkbench.noResults', { defaultValue: 'No visible actions match these filters.' })}</div> : null}
            {!(loadingList && !hasLoadedListOnce) && visibleRuns.map((run) => (
              <button type="button" aria-pressed={selectedRunId === run.id} className={`actions-workbench__row${selectedRunId === run.id ? ' actions-workbench__row--selected' : ''}`} key={run.id} onClick={() => selectRun(run.id)}>
                <span className="actions-workbench__row-status"><ActionStatusIcon status={resolveActionDisplayStatus(run)} /></span>
                <span className="actions-workbench__row-main">
                  <strong>{actionRunTitle(run, t)}</strong>
                  <small className="actions-workbench__row-meta">
                    <span>#{run.id}</span>
                    <span>{run.username || run.userId || t('actionsWorkbench.unknownUser', { defaultValue: 'Unknown user' })}</span>
                    <span>{actionPhaseDisplayName(run.phaseLabel, run.phaseCode, t)}</span>
                  </small>
                </span>
                <span className="actions-workbench__row-time">
                  <strong>{formatActionDateTime(run.createdAt, t('actionsWorkbench.notReported', { defaultValue: 'Not reported' }))}</strong>
                  <small>{run.durationMs !== undefined ? formatDuration(run.durationMs) : t('actionsWorkbench.notStarted', { defaultValue: 'Not started' })}</small>
                </span>
              </button>
            ))}
            <div className="actions-workbench__pagination" aria-label="Pagination">
              <Pagination
                current={page + 1}
                pageSize={size}
                total={visibleRunModel.totalRecords}
                showSizeChanger
                pageSizeOptions={[10, 20, 50]}
                onChange={(newPage, newSize) => {
                  setPage(newPage - 1);
                  if (newSize !== size) setSize(newSize);
                }}
                size="small"
              />
            </div>
          </div>
          <ActionDetailPanel
            detail={visibleDetail}
            loading={loadingDetail || (loadingList && !hasLoadedListOnce)}
            commandBusy={commandBusy}
            downloadingId={downloadingId}
            onRequestCancel={setPendingCancel}
            onCancelTarget={(target) => void runTargetCommand(target, 'cancel')}
            onRetryTarget={(target) => void runTargetCommand(target, 'retry')}
            onDownload={downloadArtifact}
            onCopyId={copyId}
          />
        </section>

        <ConfirmDialog
          visible={Boolean(pendingCancel)}
          onHide={() => setPendingCancel(null)}
          mode="yesNo"
          title={pendingCancel
            ? getCancelPresentation(pendingCancel.commandHints, t).label
            : t('actionsWorkbench.cancel.action', { defaultValue: 'Cancel action' })}
          message={pendingCancel?.commandHints?.cancelScope === 'ACCESSIBLE_TARGETS'
            ? t('actionsWorkbench.cancel.accessibleConfirmation', { defaultValue: 'Cancel only the accessible targets currently shown as cancellable? Other targets are not affected.' })
            : t('actionsWorkbench.cancel.entireConfirmation', { defaultValue: 'Cancel every active target in this action?' })}
          icon="warning"
          severity="warning"
          confirmLabel={t('actionsWorkbench.cancel.confirm', { defaultValue: 'Confirm cancellation' })}
          onConfirm={() => void performCancel()}
        />
      </main>
    </ConfigProvider>
  );
};

export default ActionsWorkbench;
